import React from "react";
import { Helmet } from "react-helmet";
import { Button, ShareSection } from "../../components/template";

// Hashtag section with correct color formatting
const HashtagSection = ({ tags }) => (
  <div className="mt-6 flex flex-wrap md:gap-3 gap-1 justify-center md:mb-6 mb-3">
    {tags.map((tag, index) => (
      <span
        key={index}
        className="bg-red-700 bg-opacity-10 text-red-700 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 m-1 mb-2 hover:bg-red-700 hover:text-white"
      >
        {tag}
      </span>
    ))}
  </div>
);

const Blog6 = () => {
  const hashtags = [
    "#mindfulmornings",
    "#stressrelief",
    "#selfcare",
    "#ParamLife",
    "#wellnesshabits",
  ];

  return (
    <>
      {/* 🪷 SEO Meta Tags */}
      <Helmet>
        <title>11 Tiny Morning Habits That Reduce Stress Instantly</title>
        <meta
          name="title"
          content="11 Tiny Morning Habits That Reduce Stress Instantly"
        />
        <meta
          name="description"
          content="Start your day calmer with 11 tiny morning habits to reduce stress, boost focus, and build peace — no 5 AM routine required."
        />
        <meta
          name="keywords"
          content="morning routine, stress relief habits, mindful mornings, Param Life, simple habits, stress management, mental health, Ayurveda lifestyle, calm morning, self care India"
        />
        <meta name="author" content="Param Life" />
        <meta
          property="og:title"
          content="11 Tiny Morning Habits That Reduce Stress Instantly"
        />
        <meta
          property="og:description"
          content="Start your day calmer with 11 tiny morning habits to reduce stress, boost focus, and build peace — no 5 AM routine required."
        />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Param Life" />
        <meta property="og:locale" content="en_IN" />
      </Helmet>

      {/* 🌤️ Blog Body */}
      <article className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed text-gray-800">
        {/* Header */}
        <h1 className="text-primary text-left text-3xl font-semibold mb-8">
           11 Tiny Morning Habits That Melt Away Stress
        </h1>

        <img
          src="/assets/knowyourself/blogs/blog6.jpg"
          alt="Morning routine calm"
          className="w-[80%] max-w-xl rounded-lg shadow-md mx-auto mb-8"
        />

        <p className="text-center italic text-gray-600 mb-6">
          Start your day calmer, clearer, and more centered — no fancy routines needed.
        </p>

        {/* 💫 Intro Hook */}
        <section className="space-y-4 mb-8">
          <p>
            Most people start their day already stressed. Before even leaving bed,
            they’ve scrolled through 10 messages, checked the news, and compared
            their lives with 20 strangers on social media. And then we wonder why
            our minds feel cluttered, restless, and reactive all day.
          </p>
          <p>
            But here’s the truth — stress doesn’t come from “big problems” alone.
            It’s the small, unconscious choices we make each morning that build mental chaos.
          </p>
          <p>
            The good news? Peace is also built through small choices — gentle,
            consistent ones. You don’t need an expensive morning ritual or a 5 AM
            bootcamp to feel good. All you need are tiny, conscious habits that
            align your body and mind with the natural rhythm of calm and clarity.
          </p>
          <p className="text-[#578d48] font-semibold">
            Here are 11 tiny morning habits that can reduce stress instantly — and bring back that quiet confidence we all crave.
          </p>
        </section>

        {/* 🧘‍♀️ Habits Section */}
        <section className="space-y-6 mb-8">
          <h2 className="text-secondary text-2xl font-semibold mb-6">
            11 Tiny Habits for a Calmer Morning
          </h2>

          {/* Habit 1 */}
          <div>
            <h3 className="text-primary font-semibold">1️⃣ Wake Up Without Your Phone</h3>
            <p>
              Before you open your eyes to the world, open them to yourself.
              Grabbing the phone first thing floods your brain with information,
              comparison, and dopamine rushes — before you’ve even taken your first conscious breath.
            </p>
            <p className="mt-2 text-[#578d48]">
              <strong>Try This:</strong> Keep your phone out of reach at night. Use a simple alarm clock or let light wake you.
              Spend the first few minutes just stretching or sitting in silence.
              That small pause sets the tone for a peaceful, grounded day.
            </p>
          </div>

          {/* Habit 2 */}
          <div>
            <h3 className="text-primary font-semibold">2️⃣ Sit in Silence for the First 5 Minutes</h3>
            <p>
              Your mind wakes up faster than your body. So give it a moment to settle before the noise of the world begins.
              Just five minutes of silence — no music, no plans, no “what’s next.”
              Simply notice your breath, your surroundings, and your thoughts without judgment.
            </p>
            <p className="text-[#578d48]">
              This is how you meet yourself before you meet the world.
            </p>
          </div>

          {/* Habit 3 */}
          <div>
            <h3 className="text-primary font-semibold">3️⃣ Do 5 Rounds of Deep Breathing</h3>
            <p>
              Inhale deeply through your nose, hold for a second, and exhale slowly.
              Just five conscious breaths calm your nervous system, balance oxygen flow,
              and bring your attention to the present moment — it’s your body’s reset button.
            </p>
            <p className="text-[#578d48]">
              <strong>Try This:</strong> Do it while sitting on your bed before getting up.
              You’ll feel a noticeable calm — a steady start instead of a rushed one.
            </p>
          </div>

          {/* Habit 4 */}
          <div>
            <h3 className="text-primary font-semibold">4️⃣ Set a One-Line Intention for the Day</h3>
            <p>
              Your mind needs direction — or it will wander in every direction.
              An intention isn’t a to-do list — it’s a gentle reminder of how you want to show up.
            </p>
            <p className="italic text-[#578d48]">
              “I’ll move through the day with patience.” <br />
              “I’ll focus on what matters most.” <br />
              “I’ll stay calm no matter what happens.”
            </p>
          </div>

          {/* Habit 5 */}
          <div>
            <h3 className="text-primary font-semibold">5️⃣ Make Your Bed Neatly and Mindfully</h3>
            <p>
              It’s not about the bed — it’s about starting your day with order and clarity.
              When you make your bed, you tell your brain: I’m in control of my space, my energy, and my choices.
            </p>
            <p className="text-[#578d48]">
              <strong>Try This:</strong> While arranging your bed, breathe slowly and be present in the act.
              Notice the stillness — it’s a moving meditation in disguise.
            </p>
          </div>

          {/* Habit 6 */}
          <div>
            <h3 className="text-primary font-semibold">6️⃣ Do Energizing Body Movements for 10 Minutes</h3>
            <p>
              You don’t need a gym or yoga mat. Your body simply needs to move — stretch, walk, dance, or do Surya Namaskar.
              Movement releases blocked energy, boosts blood flow, and clears brain fog.
            </p>
            <p className="text-[#578d48]">
              <strong>Try This:</strong> Put on a light instrumental track and move freely — your body will thank you.
            </p>
          </div>

          {/* Habit 7 */}
          <div>
            <h3 className="text-primary font-semibold">7️⃣ Soak in Morning Sunlight for 5 Minutes</h3>
            <p>
              Sunlight in the first hour of waking resets your rhythm, improves mood, and signals your body that it’s time to be alert naturally.
              Skip the coffee for a few minutes and soak in light instead — from your balcony or terrace.
            </p>
            <p className="text-[#578d48]">
              <strong>Try This:</strong> Stand barefoot on the ground if possible, close your eyes, and let the warmth touch your face.
              It’s nature’s antidepressant — free and powerful.
            </p>
          </div>

          {/* Habit 8 */}
          <div>
            <h3 className="text-primary font-semibold">8️⃣ Eat a Healthy Breakfast Without Distractions</h3>
            <p>
              Don’t just eat — receive your food. When you eat while scrolling, your mind can’t register satisfaction.
              That’s why you often crave more even when your stomach is full.
            </p>
            <p className="text-[#578d48]">
              <strong>Try This:</strong> Put your phone aside and taste every bite.
              Food eaten with awareness digests differently — physically and emotionally.
            </p>
          </div>

          {/* Habit 9 */}
          <div>
            <h3 className="text-primary font-semibold">9️⃣ Play Calming & Uplifting Music While Getting Ready</h3>
            <p>
              Music changes your mood faster than thoughts. Start your morning playlist with something peaceful —
              instrumental, devotional, or light classical.
            </p>
            <p className="text-[#578d48]">
              <strong>Try This:</strong> Create a 10-minute “Morning Calm” playlist.
              Let it play softly — it keeps your nervous system centered.
            </p>
          </div>

          {/* Habit 10 */}
          <div>
            <h3 className="text-primary font-semibold">🔟 Remind Yourself of the 3 Top Priorities of the Day</h3>
            <p>
              Your day doesn’t need 30 tasks — it needs clarity on three.
              When you decide your top three priorities early on, you reduce decision fatigue and anxiety.
            </p>
            <p className="text-[#578d48]">
              <strong>Try This:</strong> Write them on a sticky note and revisit them through the day.
              Stress is mostly confusion — clarity kills it.
            </p>
          </div>

          {/* Habit 11 */}
          <div>
            <h3 className="text-primary font-semibold">🌸 Greet People with a Smile and Some Kind Words</h3>
            <p>
              This one costs nothing but shifts everything. When you start your day by giving warmth — a smile, a small “good morning,”
              or a kind word — you multiply it.
            </p>
            <p className="text-[#578d48]">
              <strong>Try This:</strong> Make it a ritual — greet at least three people consciously every morning.
              You’ll notice how your heart softens, and stress melts quietly in the background.
            </p>
          </div>
        </section>

        {/* 🌼 Final Thought */}
        <section className="space-y-4 mb-10">
          <h2 className="text-secondary text-2xl font-semibold">🌼 Final Thought</h2>
          <p>
            You don’t need to “find peace.” You just need to remove the noise that blocks it.
            These tiny morning habits aren’t rules — they’re reminders.
          </p>
          <p>
            Reminders that peace is built, breath by breath, choice by choice.
            Start with two or three from this list, and watch your mornings — and your life — slowly open up.
          </p>
        </section>

        {/* Hashtags + Share Section */}
        <HashtagSection tags={hashtags} />
        <ShareSection />
      </article>
    </>
  );
};

export default Blog6;
